import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { addDays } from 'date-fns';
import getDate from 'date-fns/getDate';
import parseISO from 'date-fns/parseISO';
import { observer } from 'mobx-react';
import { Grid } from '@material-ui/core';
import {
  paymentMethod,
  paymentType as PaymentType,
  paymentMode,
  originalGSNICStates,
  soldToEffectiveDaysIntervalByState,
  policyType as PolicyTypes
} from '@ourbranch/lookups';

import { appConfig, brandConfig } from 'brand-config';
import useSession from 'core/hooks/use-session';
import { useStore } from 'core/store';
import Section from 'core/components/section';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import Checkbox from 'core/components/checkbox';
import Field from 'core/components/form/form.v2';
import {
  getRateControlDateConfig,
  shouldShowRateControlDateField,
  shouldShowHoldCheckbox
} from '../../helpers/rate-control-date';
import { MegaDownPayCard } from './mega-down-pay-card';
import withDatePicker from 'core/components/with-date-picker';
import { OfferPageTabs } from 'core/store/offer-store';
import { UpdateMortgageNotification, UpdateBillToOtherNotification } from 'common/components/mortgage-details';
import { farmDirectMonthlyNote, getFarmProductLabel, isFarmDirectMonthlyPlan } from 'common/helpers/farm-helpers';
import { canOfferMegaDownPay, isMegaDownPayRequired } from '../../helpers/mega-down-pay-helpers';
import { FutureEffectiveDateWarning } from './future-effective-date-warning';
import { SmallBillingNote } from './small-billing-note';
import useStyles from './settings.styles';

const FarmSettings = observer(function FarmSettings({ paymentMethodOptions, billingDaysOptions, paymentTypeOptions }) {
  const { offer } = useStore();
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { isAgency, canApproveUWReview } = useSession();
  const { isBix } = offer;

  // Rate Control Date hold window (issue #32130). Show the read-only RCD field + the
  // underwriter "Hold Rate Control Date" checkbox only for underwriters on brands that hold
  // (X > 0 / Y > 0). Branch (X=0/Y=0) shows neither.
  const rateControlDateConfig = getRateControlDateConfig(brandConfig);
  const showRateControlDate = shouldShowRateControlDateField(canApproveUWReview, rateControlDateConfig);
  const showHoldRateControlDate = shouldShowHoldCheckbox(canApproveUWReview, rateControlDateConfig);
  // The loaded offer lives at `offer.offer` (here `offer` is the offer store, not the offer
  // itself), so its quote is `offer.offer.quote` — same path every other consumer uses. Reading
  // `offer.quote` hit the store's own empty `quote` field and always rendered blank. (#32130)
  const rateControlDateValue = offer?.offer?.quote?.global?.rateControlDate || 'Not set';
  const { primaryMortgageDetail, interestedParties, farmInterestedParties } = values;
  const state = offer?.state;
  // Forreston/other mutuals' Home (Z) and Rental (Y) products render through these shared Farm
  // settings, so the field labels must reflect the actual product, not always say "Farm". (#33364)
  const productLabel = getFarmProductLabel(offer?.selectedOption);
  const daysOut = soldToEffectiveDaysIntervalByState(state, PolicyTypes.Farm);

  // Forreston effective date rules: agents have specific limits, underwriters have no restrictions (GitHub issue #29728)
  const effectiveDateRules = appConfig?.effectiveDateRules;
  const newBusinessRules = isAgency && effectiveDateRules?.agent?.newBusiness;
  const isUnrestrictedUnderwriter = effectiveDateRules && !isAgency;
  let effectiveDateRange = { minDate: addDays(new Date(), daysOut), maxDate: addDays(new Date(), 60) };
  if (newBusinessRules) {
    effectiveDateRange = {
      minDate: addDays(new Date(), -newBusinessRules.maxBackdateDays),
      maxDate: addDays(new Date(), newBusinessRules.maxForwardDays)
    };
  } else if (isUnrestrictedUnderwriter) {
    effectiveDateRange = { minDate: undefined, maxDate: undefined };
  }
  const showSmallBillingNote =
    values.global.farmPaymentMethod === paymentMethod.ACH && !isBix && originalGSNICStates.includes(state);

  const goToFarmDetailsTab = (location) => () => {
    offer.setCurrentTab(OfferPageTabs.FARM);
    offer.setScrollTo(location);
  };

  const offerMegaDownPay = useMemo(
    () =>
      canOfferMegaDownPay(
        values.global.farmEffectiveDate,
        values.global.farmBillingDayOfMonth,
        values.global.farmPaymentType
      ),
    [values.global.farmEffectiveDate, values.global.farmBillingDayOfMonth, values.global.farmPaymentType]
  );

  const megaDownPayRequired = useMemo(
    () =>
      isMegaDownPayRequired(
        values.global.farmEffectiveDate,
        values.global.farmBillingDayOfMonth,
        values.global.farmPaymentType
      ),
    [values.global.farmEffectiveDate, values.global.farmBillingDayOfMonth, values.global.farmPaymentType]
  );

  const isPaymentMortgage = values.global.farmPaymentMethod === paymentMethod.Escrow;
  const isPaymentBillToOther = values.global.farmPaymentMethod === paymentMethod.BillToOther;
  const isPaymentDirect = [paymentMethod.Direct, paymentMethod.BillToOther].includes(values.global.farmPaymentMethod);
  // Show the "first installment payment method" dropdown for Direct and BillToOther — both use a
  // chosen CC/ACH method to settle installment #1 (or the full annual for OneTime / BillToOther).
  const showFirstInstallmentMethod =
    values.global.farmPaymentMethod === paymentMethod.Direct ||
    values.global.farmPaymentMethod === paymentMethod.BillToOther;

  const firstInstallmentMethodOptions = useMemo(
    () => paymentMethodOptions.filter((o) => [paymentMethod.CreditCard, paymentMethod.ACH].includes(o.id)),
    [paymentMethodOptions]
  );

  // this is used to default the billing day of the month to the day that the policy is effective
  const farmEffectiveDay = useMemo(() => {
    if (values.global.farmEffectiveDate) {
      return getDate(parseISO(values.global.farmEffectiveDate));
    }
  }, [values.global.farmEffectiveDate]);

  // if home billing day of month is null, default the selection to the effective day of the policy
  // or the 28th if the effectiveDay is past the allowed options of days 1 - 28
  useEffect(() => {
    if (!values.global.farmBillingDayOfMonth && values.global.farmBillingDayOfMonth !== farmEffectiveDay) {
      if (farmEffectiveDay > 28) {
        setFieldValue('global.farmBillingDayOfMonth', 28);
      } else {
        setFieldValue('global.farmBillingDayOfMonth', farmEffectiveDay);
      }
    }
  }, [values.global.farmBillingDayOfMonth, farmEffectiveDay, setFieldValue]);

  // A migrated Direct + Monthly plan keeps Monthly listed so the select shows it (#37291). Whether the
  // offer has that plan comes from the saved offer, not the form, so an edit can't create one.
  const savedGlobal = offer?.offer?.quote?.global;
  const keepsMigratedDirectMonthly = isFarmDirectMonthlyPlan({
    method: savedGlobal?.farmPaymentMethod,
    type: savedGlobal?.farmPaymentType
  });
  // The form still holds that migrated plan: drives the note and keeping Monthly on a move to autopay.
  const isDirectMonthlyPlan =
    keepsMigratedDirectMonthly &&
    isFarmDirectMonthlyPlan({ method: values.global.farmPaymentMethod, type: values.global.farmPaymentType });

  const onFarmPaymentChange = useCallback(
    // set the frequency
    (farmPaymentMethod) => {
      const paymentTypeLookup = {
        [paymentMethod.Escrow]: PaymentType.Escrow
      };
      // Moving a Direct + Monthly member onto a card or bank withdrawal keeps them Monthly, so switching
      // to automatic payments is one change rather than also re-picking the frequency (#37291).
      const keepsMonthly =
        isDirectMonthlyPlan && [paymentMethod.CreditCard, paymentMethod.ACH].includes(farmPaymentMethod);
      setFieldValue(
        'global.farmPaymentType',
        keepsMonthly ? PaymentType.Monthly : (paymentTypeLookup[farmPaymentMethod] ?? PaymentType.OneTime)
      );

      // set the payment mode
      if ([paymentMethod.Direct, paymentMethod.BillToOther].includes(farmPaymentMethod)) {
        setFieldValue('global.farmPaymentMode', paymentMode.Direct);
      }
      if ([paymentMethod.CreditCard, paymentMethod.ACH, paymentMethod.Escrow].includes(farmPaymentMethod)) {
        setFieldValue('global.farmPaymentMode', paymentMode.Automated);
      }
      if (farmPaymentMethod !== paymentMethod.Direct && farmPaymentMethod !== paymentMethod.BillToOther) {
        setFieldValue('global.directBillPreferredPaymentMethod', null);
      }
    },
    [setFieldValue, isDirectMonthlyPlan]
  );

  // BillToOther parties on Farm policies live in `farmInterestedParties` (added via the Farm
  // details page's interested-parties FormList). Fall back to the general array for safety.
  const billToOtherParty =
    farmInterestedParties?.find((party) => party.relationship === 'billToOther') ||
    interestedParties?.find((party) => party.relationship === 'billToOther');

  return (
    <Section title={`${productLabel} Policy Settings`} type="SubSection">
      <Card className={classes.overflowContainer} type="secondary" id="homeSettings">
        <Grid container>
          <Label type="formSubTitle" className={classes.subtitle}>
            Policy Term
          </Label>
        </Grid>
        <Grid container>
          <Field
            type="date"
            name="global.farmEffectiveDate"
            label={`${productLabel} policy effective date`}
            xs={4}
            mode="dark"
            disableFuture={false}
            minDate={effectiveDateRange.minDate}
            maxDate={effectiveDateRange.maxDate}
            fast={false}
          />
          {showRateControlDate && (
            <Field
              type="value"
              name="global.rateControlDate"
              label="Rate Control Date"
              xs={4}
              mode="dark"
              value={rateControlDateValue}
            />
          )}
          <Grid item xs={12}>
            <FutureEffectiveDateWarning daysOut={daysOut} />
          </Grid>
          {showHoldRateControlDate && (
            <Grid item xs={12}>
              <Checkbox
                id="holdRateControlDate"
                mode="dark"
                label={`Hold Rate Control Date Up To ${rateControlDateConfig.underwriterMaxHoldDays} Days`}
                value={offer.holdRateControlDate}
                onChange={(e) => offer.setHoldRateControlDate(e.target.checked)}
              />
            </Grid>
          )}
        </Grid>
        <Grid container alignItems="center" className={classes.billingDetails} spacing={2} xs={12}>
          <Grid item xs={2}>
            <Label type="formSubTitle" className={classes.subtitle}>
              Billing Details
            </Label>
          </Grid>
          <SmallBillingNote show={showSmallBillingNote} />
        </Grid>
        <Grid container spacing={4}>
          <Field
            label={`${productLabel} payment method`}
            xs={4}
            type="select"
            name="global.farmPaymentMethod"
            options={paymentMethodOptions}
            onChange={onFarmPaymentChange}
            mode="dark"
            // onFarmPaymentChange reads the current frequency; a FastField would keep a stale copy of it.
            fast={false}
          />
          <Field
            label={`${productLabel} payment frequency`}
            xs={4}
            type="select"
            name="global.farmPaymentType"
            options={paymentTypeOptions.filter((option) => {
              if (option.id === PaymentType.Escrow) {
                return isPaymentMortgage;
              }
              if (
                option.id === PaymentType.Monthly &&
                values.global.farmPaymentMethod === paymentMethod.Direct &&
                !keepsMigratedDirectMonthly
              ) {
                return false;
              }
              return true;
            })}
            mode="dark"
            disabled={isPaymentMortgage || isPaymentBillToOther}
            onChange={(val) => {
              if (
                val === PaymentType.Monthly &&
                [paymentMethod.ACH, paymentMethod.CreditCard].includes(values.global.farmPaymentMethod)
              ) {
                setFieldValue('global.farmPaymentMode', paymentMode.Automated);
              }
              if (val !== PaymentType.Monthly) {
                setFieldValue('global.farmMegaDownPay', false);
              }
            }}
            fast={false}
          />
          {[PaymentType.Monthly, PaymentType.QuarterTerm, PaymentType.HalfTerm].includes(
            values.global?.farmPaymentType
          ) && (
            <Field
              type="select"
              name="global.farmBillingDayOfMonth"
              label={`${productLabel} Billing Day`}
              mode="dark"
              options={billingDaysOptions}
              xs={4}
              fast={false}
            />
          )}
        </Grid>
        {isPaymentDirect &&
          [PaymentType.QuarterTerm, PaymentType.HalfTerm].includes(values.global?.farmPaymentType) && (
            <Grid className={classes.directBillingDescription}>
              An invoice will be sent 30 days earlier than the billing day in order to pay it before it's due.
            </Grid>
          )}
        {isDirectMonthlyPlan && (
          <Grid className={classes.directBillingDescription}>
            <strong>{farmDirectMonthlyNote.header}.</strong> {farmDirectMonthlyNote.body}
          </Grid>
        )}
        {showFirstInstallmentMethod && (
          <Grid container spacing={4}>
            <Field
              label="First charge payment method"
              xs={5}
              type="select"
              name="global.directBillPreferredPaymentMethod"
              options={firstInstallmentMethodOptions}
              mode="dark"
            />
          </Grid>
        )}
        {isPaymentMortgage && (
          <Grid container spacing={4}>
            <Field
              name="primaryMortgageDetail.mortgageHolderName"
              type="value"
              label="Lender Name"
              mode="dark"
              xs={4}
              value={
                primaryMortgageDetail.mortgageHolderName ? primaryMortgageDetail.mortgageHolderName : 'Not provided'
              }
            />
            <Field
              name="primaryMortgageDetail.loanNumber"
              type="value"
              label="Loan Number"
              mode="dark"
              xs={4}
              value={primaryMortgageDetail.loanNumber ? primaryMortgageDetail.loanNumber : 'Not provided'}
            />
            <Field
              name="primaryMortgageDetail.mortgageHolderAddress.address"
              type="value"
              label="Lender Address"
              xs={4}
              mode="dark"
              value={
                primaryMortgageDetail.mortgageHolderAddress?.address
                  ? `${primaryMortgageDetail.mortgageHolderAddress.address}, ${primaryMortgageDetail.mortgageHolderAddress.city}, 
                      ${primaryMortgageDetail.mortgageHolderAddress.state}, ${primaryMortgageDetail.mortgageHolderAddress.zip}`
                  : 'Not provided'
              }
            />
            <Grid item xs={12}>
              <UpdateMortgageNotification
                isMortgageAdded={!!primaryMortgageDetail.mortgageHolderName}
                onNavigateToDetails={goToFarmDetailsTab('addMortgage')}
              />
            </Grid>
          </Grid>
        )}
        {isPaymentBillToOther && (
          <Grid container spacing={4}>
            <Field
              type="value"
              label="bill to other name or company"
              mode="dark"
              xs={6}
              value={billToOtherParty?.name ? billToOtherParty?.name : 'Not provided'}
            />

            <Field
              type="value"
              label="Bill to other address"
              xs={6}
              mode="dark"
              value={
                billToOtherParty?.address
                  ? `${billToOtherParty?.address.address}, ${billToOtherParty?.address.city}, 
                      ${billToOtherParty?.address.state}, ${billToOtherParty?.address.zip}`
                  : 'Not provided'
              }
            />
            <Grid item xs={12}>
              <UpdateBillToOtherNotification
                isBillToOtherAdded={!!billToOtherParty?.name}
                onNavigateToDetails={goToFarmDetailsTab('addInterestedParty')}
              />
            </Grid>
          </Grid>
        )}
        {(offerMegaDownPay || megaDownPayRequired) && (
          <MegaDownPayCard megaDownPayRequired={megaDownPayRequired} formikValue="global.farmMegaDownPay" />
        )}
      </Card>
    </Section>
  );
});

FarmSettings.propTypes = {
  paymentMethodOptions: PropTypes.array.isRequired,
  billingDaysOptions: PropTypes.array.isRequired,
  paymentTypeOptions: PropTypes.array.isRequired
};

export default withDatePicker(FarmSettings);
